/**
 * DispensingQueue.tsx — the pharmacy console's queue section.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY three tabs and not one filtered list: pending / dispensed / history mirror
 * the flow the dispensary already works to — what needs doing now, what has been
 * done but not yet handed to a courier, and the bounded record of everything
 * else. Keeping "dispensed but not yet dispatched" its own tab is what stops an
 * order being dispensed and then quietly forgotten before it ships.
 *
 * The row → Drawer master-detail is the house pattern (the admin payments and
 * CRM pipeline surfaces open their detail the same way), reusing `ui/kit`'s
 * Drawer and `ui/Table`'s DataTable rather than re-deriving either.
 *
 * MONEY-FREE: the queue shape carries no price field. There is nothing here to
 * redact, hide or remember not to render.
 *
 * Every fetch is retryable (TA-06) and a tab switch re-reads, so a dispense done
 * in the drawer is reflected without a page reload.
 */
import { useCallback, useEffect, useState } from 'react';
import type { PharmacyOrderSummary, PharmacyQueueTab } from '../api/types';
import { getPharmacyQueue } from '../api/pharmacy';
import { formatUtcDateTime } from '../lib/format';
import { Badge, Card, Drawer, ErrorState, Help } from '../ui/kit';
import { SkeletonCard } from '../ui/Skeleton';
import { DataTable, Td, Th } from '../ui/Table';
import { Tabs, TabPanel, type TabDef } from '../ui/Tabs';
import { SectionTitle } from '../ui/sections';
import { toneFor } from '../ui/tones';
import { FilterChip, FilterChipRow, FiltersTrigger } from '../ui/filters';
import { pharmacyQueueLabel } from './pharmacy-labels';
import { OrderDetailPanel } from './OrderDetailPanel';
import {
  EMPTY_PHARMACY_FILTERS,
  PHARMACY_TEST_CHIP,
  PharmacyFilters,
  activePharmacyFilterCount,
  type PharmacyFilterState,
} from './PharmacyFilters';
import { TestRecordBadge } from './TestRecordNotice';

const TABS: TabDef[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'dispensed', label: 'Dispensed' },
  { id: 'history', label: 'History' },
];

/** Per-tab empty copy — a queue with nothing in it is good news, not an error. */
const EMPTY_COPY: Record<PharmacyQueueTab, string> = {
  pending: 'Nothing waiting to be dispensed. New orders appear here as prescriptions are issued.',
  dispensed: 'Nothing dispensed yet that is still waiting for a courier.',
  history: 'No dispensing history in the last 90 days.',
};

function isTab(value: string): value is PharmacyQueueTab {
  return value === 'pending' || value === 'dispensed' || value === 'history';
}

/** Minimum the drawer needs — the queue row carries more, but activity/deep-links may not. */
type OpenOrder = {
  orderCaseId: string;
  orderRef: string;
  /**
   * The row's `itemLabel` — the plan token the server lists this order under
   * (`itemLabel: r.productKey ?? '—'` in orders' pharmacy service, on BOTH
   * deployed builds). The drawer needs it to tell a real dispensing name from a
   * machine default, because the DETAIL payload carries no product key at all:
   * see `lib/prescribedLine#resolvePharmacyLine`.
   *
   * `null` on the two paths that have no queue row to read it off — an activity
   * row and a `?order=` deep link into an order that is not on the tab currently
   * loaded. The console then simply cannot apply that one rule and renders the
   * name as the wire states it, exactly as it did before this lane; it never
   * guesses a label. A pharmacist who arrives by either of those routes has, by
   * definition, not just read the Item column.
   */
  itemLabel: string | null;
};

export function DispensingQueue({
  focusOrderCaseId = null,
  focusOrderRef = null,
  onFocusConsumed,
}: {
  /** `?order=<orderCaseId>` — opens the drawer even when the order is off the current tab. */
  focusOrderCaseId?: string | null;
  /** Optional ref from the activity row or URL until the detail read lands. */
  focusOrderRef?: string | null;
  /** Called once the focus drawer opens so `?order=` can be cleared from the URL. */
  onFocusConsumed?: () => void;
}) {
  const [tab, setTab] = useState<PharmacyQueueTab>('pending');
  const [items, setItems] = useState<PharmacyOrderSummary[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // The OPEN ROW ITSELF, not just its id. A dispense moves the order out of the
  // tab it was opened from, so deriving the drawer's subject from the reloaded
  // list would slam it shut at exactly the moment the pharmacist still has work
  // to do on it — capturing the courier and tracking.
  const [openOrder, setOpenOrder] = useState<OpenOrder | null>(null);
  // APPLIED, not draft — the panel holds its own draft and commits once, so the
  // queue re-reads on Apply rather than on every tick. `showTest` is the only
  // facet, and it is the whole of what the browser gets to say: the server
  // decides scope and refuses every write regardless (R41).
  const [filters, setFilters] = useState<PharmacyFilterState>(EMPTY_PHARMACY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const load = useCallback(() => {
    setStatus('loading');
    getPharmacyQueue(tab, filters.showTest)
      .then((response) => {
        setItems(response.items);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [tab, filters.showTest]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!focusOrderCaseId) return;
    // WAIT FOR THE QUEUE BEFORE CONSUMING THE DEEP LINK (2026-09-13). This effect
    // used to run on mount, while `items` was still null because the fetch had not
    // resolved, take `itemLabel ?? null`, and then call onFocusConsumed() — which
    // strips `order` from the URL and nulls focusOrderCaseId, so the effect never
    // ran again once the rows arrived. EVERY `?order=` deep link therefore opened
    // the drawer with no label, including orders sitting on the loaded tab, and
    // without a label the machine-default name rule cannot fire: the console
    // printed the raw product key as the medication, on the one screen a
    // pharmacist picks stock from. Returning while the load is in flight costs a
    // render and closes that route.
    // Wait for the load to SETTLE — not for rows to exist. `status === 'loading'`
    // is the only state in which the label might still be coming; 'error' and an
    // empty 'ready' both mean there is no label to be had, and the drawer must
    // still open on those (a failed queue read must never swallow a deep link).
    if (status === 'loading') return;
    const fromQueue = items?.find((item) => item.orderCaseId === focusOrderCaseId);
    const orderRef = fromQueue?.orderRef ?? focusOrderRef ?? '';
    // If the focused order IS on the loaded tab, its label is right here and the
    // drawer gets the same protection a clicked row gets.
    setOpenOrder({ orderCaseId: focusOrderCaseId, orderRef, itemLabel: fromQueue?.itemLabel ?? null });
    onFocusConsumed?.();
  }, [focusOrderCaseId, focusOrderRef, items, status, onFocusConsumed]);

  const syncOrderRef = useCallback((orderRef: string) => {
    setOpenOrder((prev) => (prev ? { ...prev, orderRef } : prev));
  }, []);

  return (
    <>
      <SectionTitle>Dispensing queue</SectionTitle>
      <Help>
        Orders assigned to this pharmacy. Open an order to see the prescription and the patient&rsquo;s
        details.
      </Help>

      {/* The filter bar sits ABOVE the tabs, not inside one, because the facet
          applies to all three: an order hidden from Pending must be hidden from
          History too, and a control that lived on a tab would imply otherwise.
          `alignItems: 'flex-start'` because a wrapped chip block is allowed to
          be taller than the row — see FilterChipRow. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: '8px',
          marginBottom: 'var(--gps-s-3)',
        }}
      >
        <FiltersTrigger
          appliedCount={activePharmacyFilterCount(filters)}
          onOpen={() => setFiltersOpen(true)}
          onClearAll={() => setFilters(EMPTY_PHARMACY_FILTERS)}
        />
        <FilterChipRow>
          {filters.showTest ? (
            <FilterChip label={PHARMACY_TEST_CHIP} onRemove={() => setFilters(EMPTY_PHARMACY_FILTERS)} />
          ) : null}
        </FilterChipRow>
      </div>

      {filtersOpen ? (
        <PharmacyFilters
          applied={filters}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => {
            setFilters(next);
            setFiltersOpen(false);
            // Any open drawer closes: its order may be leaving the list, and a
            // drawer over a list that no longer contains its subject is the same
            // disorientation the tab switch below avoids.
            setOpenOrder(null);
          }}
        />
      ) : null}

      <Tabs
        label="Dispensing queue tabs"
        tabs={TABS}
        activeId={tab}
        idBase="pharmacy-queue"
        onChange={(id) => {
          if (!isTab(id)) return;
          // Close any open detail first: the drawer's order may not exist in the
          // tab being switched to, and a drawer over an unrelated list is
          // disorientating.
          setOpenOrder(null);
          setTab(id);
        }}
      />

      {TABS.map((definition) => (
        <TabPanel key={definition.id} idBase="pharmacy-queue" tabId={definition.id} activeId={tab}>
          <Card>
            {status === 'loading' ? (
              <SkeletonCard lines={4} label="Loading the dispensing queue…" />
            ) : status === 'error' || !items ? (
              <ErrorState message="Sorry — we could not load the dispensing queue." onRetry={load} />
            ) : items.length === 0 ? (
              <Help style={{ marginBottom: 0 }}>
                {EMPTY_COPY[tab]}
                {/* An empty tab WITH the filter on means there are no test orders
                    either, which is a different fact from an empty working queue
                    and worth saying — otherwise a pharmacist who switched the
                    filter on looking for an order named in a disregard email is
                    left unsure whether the filter did anything. */}
                {filters.showTest ? ' No test orders here either.' : ''}
              </Help>
            ) : (
              <DataTable
                label={`${definition.label} orders`}
                head={
                  <>
                    <Th>Order</Th>
                    <Th>Clinic</Th>
                    <Th>Patient</Th>
                    <Th>Item</Th>
                    <Th>Status</Th>
                    <Th style={{ whiteSpace: 'nowrap' }}>Received</Th>
                  </>
                }
              >
                {items.map((item) => (
                  <tr key={item.orderCaseId}>
                    <Td>
                      {/* The order ref is the row's action — a real button, so
                          the row is reachable and operable from the keyboard
                          (a click handler on <tr> is neither). */}
                      <button
                        type="button"
                        onClick={() =>
                          setOpenOrder({
                            orderCaseId: item.orderCaseId,
                            orderRef: item.orderRef,
                            itemLabel: item.itemLabel,
                          })
                        }
                        style={{
                          minHeight: '44px',
                          padding: '0 2px',
                          border: 0,
                          background: 'none',
                          color: 'var(--gps-link)',
                          font: 'inherit',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {item.orderRef}
                      </button>
                    </Td>
                    <Td>{item.clinicName}</Td>
                    <Td>{item.patientName}</Td>
                    <Td>
                      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                        {item.itemLabel}
                        {/* Cold chain is a dispensing-safety cue, so it rides on
                            the LIST, not only in the detail — a pharmacist
                            should see it before they pick the order. The test
                            marker rides here for the SAME reason and sits FIRST:
                            "this is not real" outranks every other property of
                            the row, and it has to be readable before the order is
                            picked rather than after it is opened. */}
                        {item.testRecord ? <TestRecordBadge /> : null}
                        {item.coldChain ? <Badge tone="pending">Cold chain</Badge> : null}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={toneFor(item.state === 'dispatched' ? 'dispensed' : item.state)}>
                        {pharmacyQueueLabel(item.state)}
                      </Badge>
                    </Td>
                    <Td style={{ whiteSpace: 'nowrap' }}>{formatUtcDateTime(item.receivedAt)}</Td>
                  </tr>
                ))}
              </DataTable>
            )}
          </Card>
        </TabPanel>
      ))}

      <Drawer
        open={openOrder !== null}
        onClose={() => setOpenOrder(null)}
        width="twoThirds"
        title={openOrder?.orderRef ? `Order ${openOrder.orderRef}` : 'Order'}
      >
        {openOrder ? (
          <OrderDetailPanel
            orderCaseId={openOrder.orderCaseId}
            // The token the pharmacist just read in the Item column, carried into
            // the drawer so the Medication cell cannot simply repeat it back.
            queueItemLabel={openOrder.itemLabel}
            // Whatever the list is showing, the drawer has to be able to open.
            // Without this a revealed test row opens onto a 404 — the server
            // hides a flagged order from the detail read too unless it is asked
            // for, and a filter that shows you something you cannot open is
            // worse than no filter.
            includeTest={filters.showTest}
            // A dispense/reject/dispatch changes which tab the order belongs to,
            // so the queue behind the drawer re-reads rather than showing a row
            // that has moved on.
            onChanged={load}
            onOrderRefLoaded={syncOrderRef}
          />
        ) : null}
      </Drawer>
    </>
  );
}
